import React, { useEffect, useMemo, useState } from 'react'
import './PropertyDetailPage.css'
import MenuBar from '../components/MenuBar'
import Header from '../components/Header'
import CircularProgress from '@mui/material/CircularProgress'
import { useLocation, useSearchParams } from 'react-router-dom'
import { GoDatabase } from 'react-icons/go'
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa'
import { MdRadioButtonUnchecked } from 'react-icons/md'
import { toast } from 'react-toastify'
import httpClient from '../components/helpers/httpClient'
import { formatPropertyIdentifier, normalizeCapakey } from '../components/helpers/propertyIdentifier'

const EMPTY_LABEL = 'content missing'

const PROJECT_TYPE_LABELS = {
  OMV2019_AANVRAAG: 'Aanvraag omgevingsvergunning',
}

const DOSSIERSTUK_TYPE_LABELS = {
  ANDER_DOSSIERSTUK: 'Ander dossierstuk',
  NOTAARCHITECT: 'Nota architect',
  NOTA_ONROEREND_ERFGOED: 'Nota onroerend erfgoed',
  WERKEN_REEDS_GESTART: 'Werken reeds gestart',
}

const FILE_SOURCE_LABELS = {
  'plannen-en-fotos': 'Plannen en fotos',
  dossierstuk: 'Dossierstuk',
}

const PLAN_TYPE_LABELS = {
  BOUWPLAN_D: 'Detail',
  BOUWPLAN_G: 'Gevel',
  BOUWPLAN_I: 'Inplanting',
  BOUWPLAN_L: 'Legende',
  BOUWPLAN_P: 'Plan',
  BOUWPLAN_S: 'Snede',
  BOUWPLAN_T: 'Terreinprofiel',
  FOTO: 'Foto',
}

const PLAN_STATE_LABELS = {
  B: 'Bestaande toestand',
  N: 'Nieuwe toestand',
}

const APPLICATION_SUBPART_TYPE_LABELS = {
  GEBOUW: 'Gebouw of Constructie',
  CONSTRUC_SECUND: 'Bijgebouw of Beperkte constructie rond een gebouw',
}

const APPLICATION_ACTION_LABELS = {
  VERBOUW_MET_ZF_ZW: 'Verbouwen en/of uitbreiden zonder functiewijziging en zonder wijziging van aantal woongelegenheden',
  NIEUW_SECUND_MET: 'Nieuwbouw van bijgebouwen en niet overdekte lage constructie',
}

const getPropertyValidationLabel = (status) => {
  if (status === 'validated') return 'Validated'
  if (status === 'not_found') return 'No official match'
  if (status === 'invalid_format') return 'Invalid format'
  if (status === 'service_error') return 'Validation unavailable'
  return 'Not validated'
}

const resolvePropertyDataStatus = (property) => {
  if (!property) return false
  if (typeof property.has_data === 'boolean') return property.has_data
  if (typeof property.has_historic_data === 'boolean') return property.has_historic_data
  if (typeof property.historic_data_available === 'boolean') return property.historic_data_available
  if (typeof property.data_status === 'string') {
    return property.data_status === 'available' || property.data_status === 'has_data'
  }
  return false
}

const ValidationStatusIcon = ({ status }) => {
  if (status === 'validated') {
    return <FaCheckCircle className='property-validation-icon is-valid' title='Validated' />
  }

  if (status === 'not_found' || status === 'invalid_format' || status === 'service_error') {
    return <FaTimesCircle className='property-validation-icon is-invalid' title={getPropertyValidationLabel(status)} />
  }

  return <MdRadioButtonUnchecked className='property-validation-icon is-unvalidated' title='Not validated' />
}

const isMissingValue = (value) => {
  if (value === null || value === undefined) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.length === 0
  return false
}

const ContentMissingTag = () => (
  <span className='property-detail-page__missing-tag'>{EMPTY_LABEL}</span>
)

const StatusTag = ({ value }) => {
  if (isMissingValue(value)) return <ContentMissingTag />
  return <span className='property-detail-page__status-tag'>{value}</span>
}

const FieldValue = ({ value, children }) => {
  if (isMissingValue(value) && isMissingValue(children)) return <ContentMissingTag />
  return <span>{children ?? value}</span>
}

const DetailField = ({ label, value, children }) => (
  <div className='property-detail-page__detail-field'>
    <div className='property-detail-page__detail-label'>{label}</div>
    <div className='property-detail-page__detail-value'>
      <FieldValue value={value}>{children}</FieldValue>
    </div>
  </div>
)

const parseCapakeys = (value) => {
  if (!value) return []
  const matches = String(value).match(/\d{5}[A-Z]\d{4}\/\d{2}[A-Z0-9]{4}/gi)
  return matches ? matches.map(normalizeCapakey) : []
}

const getExportPayload = (projectExport) => projectExport?.export || projectExport || {}

const dedupeFiles = (files) => {
  const seen = new Set()
  return (files || []).filter((file) => {
    const key = file?.object_key || file?.hash || file?.uuid || file?.bestandsnaam
    if (!key) return true
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

const getRequestContentType = (object, project, application) => (
  object?.request_content_type_label ||
  object?.requestContentType ||
  object?.inhoud_aanvraag_type ||
  object?.inhoudAanvraagType ||
  application?.application_type ||
  project?.request_content_type_label ||
  project?.requestContentType ||
  project?.inhoud_aanvraag_type ||
  null
)

const getRequestSubparts = (object, applicationSubparts) => (
  applicationSubparts ||
  object?.subparts ||
  object?.request_subparts ||
  object?.inhoud_aanvraag_subparts ||
  object?.handelingen ||
  []
).map((subpart, index) => ({
  key: subpart?.key || subpart?.uuid || `subpart-${index}`,
  planAanduiding: subpart?.planAanduiding || subpart?.plan_aanduiding || subpart?.planaanduiding || subpart?.kaart_label || subpart?.description,
  type: subpart?.type || subpart?.type_label || APPLICATION_SUBPART_TYPE_LABELS[subpart?.aard_code] || subpart?.aard_code,
  handeling: subpart?.handeling || subpart?.handeling_label || APPLICATION_ACTION_LABELS[subpart?.handeling_type_code] || subpart?.handeling_type_code,
}))

const formatPhaseStatus = (status) => {
  if (status === 'decision') return 'Decision'
  if (status === 'no_decision') return 'No decision'
  return status
}

const getDecisionDocument = (decision, files) => {
  const documentUuid = decision?.document_file_uuid
  if (!documentUuid) return null
  return (files || []).find((file) => file?.uuid === documentUuid) || null
}

const getFileDownloadUrl = (file) => file?.download_url || file?.downloadUrl || file?.url || file?.public_url || null

const DocumentFieldValue = ({ file }) => {
  if (!file) return <ContentMissingTag />

  const downloadUrl = getFileDownloadUrl(file)
  if (!downloadUrl) return <span>{file.bestandsnaam || 'Document'}</span>

  return (
    <button
      type='button'
      className='property-detail-page__document-link'
      onClick={() => window.open(downloadUrl, '_blank', 'noopener,noreferrer')}
    >
      {file.bestandsnaam || 'Download document'}
    </button>
  )
}

const mapProcedurePhases = (procedure, decisions, files, project) => {
  if (procedure?.length > 0) {
    return [...procedure]
      .sort((a, b) => (a?.phase_order || 0) - (b?.phase_order || 0))
      .map((phase) => {
        const decision = (decisions || []).find((item) => item?.phase_code === phase.phase_code) || null
        const decisionDocument = getDecisionDocument(decision, files)

        const isAppealPhase = phase.phase_code === 'FASE_BEROEPSPERIODE_EA'
        const fields = isAppealPhase ? [
          ['Appeal deadline', phase.appeal_deadline],
          ['Appeal document', null, { file: null }],
        ] : [
          ['Public inquiry start', phase.public_inquiry_start],
          ['Public inquiry end', phase.public_inquiry_end],
          ['Decision status', formatPhaseStatus(phase.decision_status)],
          ['Decision date', phase.decision_date || decision?.decision_date],
          ['Decision summary', phase.decision_summary],
          ['Decision nature', decision?.decision_nature],
          ['Decision scope', decision?.decision_scope],
          ['Decision document', decisionDocument?.bestandsnaam, { file: decisionDocument }],
        ]

        return {
          key: phase.phase_code || phase.phase_label,
          label: phase.phase_label,
          status: phase.decision_status,
          fields,
        }
      })
  }

  return [
    {
      key: 'eerste-aanleg',
      label: 'Eerste aanleg',
      status: null,
      fields: [
        ['Decision date', project.beslissing_datum],
        ['Aard van de beslissing', project.beslissing_tekst],
        ['Decision scope', null],
        ['Decision document', null],
      ],
    },
    {
      key: 'beroepsfase',
      label: 'Beroepsfase',
      status: null,
      fields: [
        ['Beroepsinstantie', project.beroeps_instantie],
        ['End date beroep', project.eind_datum_beroep],
        ['Appeal status', null],
        ['Appeal documents', null],
      ],
    },
    {
      key: 'laatste-aanleg',
      label: 'Laatste aanleg',
      status: null,
      fields: [
        ['Status', null],
        ['End date', null],
        ['Decision date', null],
        ['Decision document', null],
      ],
    },
  ]
}

const mapHistoricProjects = (historicExport) => {
  return (historicExport?.project_exports || []).map((projectExport, index) => {
    const exportData = getExportPayload(projectExport)
    const project = exportData.project || {}
    const application = exportData.application || {}
    const applicationSubparts = exportData.application_subparts || []
    const procedure = exportData.procedure || []
    const decisions = exportData.decisions || []
    const contents = exportData.contents || []
    const objects = exportData.objects || []
    const dossierstukken = exportData.dossierstukken || []
    const files = exportData.files || []
    const activeContent = contents.find((content) => content?.actief) || contents[0] || {}
    const plansAndPhotos = files.filter((file) => file?.source === 'plannen-en-fotos')
    const dossierFiles = files.filter((file) => file?.source === 'dossierstuk')
    const objectDescriptions = objects.map((object) => object?.betreft).filter(Boolean)
    const fallbackDescription = objectDescriptions[0] || project.project_naam || null

    return {
      key: project.uuid || project.projectnummer || `project-${index}`,
      project,
      contents,
      activeContent,
      objects,
      dossierstukken,
      files,
      application,
      applicationSubparts,
      procedure,
      decisions,
      plansAndPhotos,
      dossierFiles,
      basic: {
        title: project.project_naam,
        projectNumber: project.projectnummer,
        description: fallbackDescription,
        requestDate: project.request_date,
        projectTypeCode: project.project_type_code,
        projectTypeLabel: PROJECT_TYPE_LABELS[project.project_type_code] || null,
        requester: null,
        status: project.procedure_status,
        phase: project.procedure_phase,
        behandelendeOverheid: project.behandelende_overheid,
        beroepsInstantie: project.beroeps_instantie,
        inzageGegevensType: project.inzage_gegevens_type,
        projectinhoudVersie: activeContent.projectinhoud_versie,
      },
      phases: mapProcedurePhases(procedure, decisions, files, project),
      requestParts: objects.map((object, objectIndex) => {
        const objectPlansAndPhotos = plansAndPhotos.filter((file) => !file.object_uuid || file.object_uuid === object.uuid)
        const objectDossierstukken = dossierstukken
          .filter((stuk) => !stuk.object_uuid || stuk.object_uuid === object.uuid)
          .map((stuk) => ({
            ...stuk,
            files: dedupeFiles(dossierFiles.filter((file) => file.dossierstuk_uuid === stuk.uuid)),
          }))

        return {
          key: object.uuid || `request-part-${objectIndex}`,
          type: getRequestContentType(object, project, application),
          title: object.kaart_label || object.omschrijving,
          description: object.betreft,
          capakeys: parseCapakeys(object.kadastrale_informatie),
          plansAndPhotos: objectPlansAndPhotos,
          dossierstukken: objectDossierstukken,
          subparts: getRequestSubparts(object, applicationSubparts.filter((subpart) => !subpart.parent_object_uuid || subpart.parent_object_uuid === object.uuid)),
        }
      }),
    }
  })
}

const FileRow = ({ file }) => {
  const typeCode = file?.extra_data?.tekeningsoort?.code
  const stateCode = file?.extra_data?.toestand?.code
  const downloadUrl = getFileDownloadUrl(file)
  const meta = [
    FILE_SOURCE_LABELS[file?.source] || file?.source,
    PLAN_TYPE_LABELS[typeCode] || typeCode,
    PLAN_STATE_LABELS[stateCode] || stateCode,
    file?.datum_opladen ? `Uploaded ${file.datum_opladen}` : null,
    file?.mime_type,
  ].filter(Boolean)

  return (
    <button
      type='button'
      className={`property-detail-page__file-row ${downloadUrl ? 'is-downloadable' : ''}`}
      onClick={() => {
        if (downloadUrl) {
          window.open(downloadUrl, '_blank', 'noopener,noreferrer')
        } else {
          toast.info('Download URL is missing from the backend data.')
        }
      }}
    >
      <div>
        <div className='property-detail-page__file-name'>{file?.bestandsnaam || 'Unnamed file'}</div>
        {meta.length > 0 && <div className='property-detail-page__file-meta'>{meta.join(' · ')}</div>}
      </div>
      <div className='property-detail-page__file-download-state'>
        {downloadUrl ? 'Download' : <ContentMissingTag />}
      </div>
    </button>
  )
}

const ProjectDetailPanel = ({
  mappedProject,
  activeTab,
  setActiveTab,
  expandedPhaseKey,
  setExpandedPhaseKey,
  selectedPartKey,
  setSelectedPartKey,
}) => {
  const [expandedRequestSections, setExpandedRequestSections] = useState({})

  useEffect(() => {
    setExpandedRequestSections({})
  }, [selectedPartKey])

  const toggleRequestSection = (key) => {
    setExpandedRequestSections((currentSections) => ({
      ...currentSections,
      [key]: !currentSections[key],
    }))
  }

  if (!mappedProject) {
    return (
      <div className='property-detail-page__mapped-view'>
        <div className='property-detail-page__empty'>Select a project to view the project data.</div>
      </div>
    )
  }

  const selectedPart = mappedProject.requestParts.find((part) => part.key === selectedPartKey) || null

  return (
    <div className='property-detail-page__mapped-view'>
      <div className='property-detail-page__project-tabs'>
        {[
          ['basic', 'basic details'],
          ['procedure', 'procedure'],
          ['request', 'inhoud aanvraag'],
        ].map(([key, label]) => (
          <button
            type='button'
            className={activeTab === key ? 'selected' : ''}
            key={key}
            onClick={() => setActiveTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'basic' && (
        <div className='property-detail-page__line-list'>
            <DetailField label='Title' value={mappedProject.basic.title} />
            <DetailField label='Date request' value={mappedProject.basic.requestDate} />
            <DetailField label='Project type' value={mappedProject.basic.projectTypeLabel || mappedProject.basic.projectTypeCode} />
            <DetailField label='Status' value={mappedProject.basic.status}>
              <StatusTag value={mappedProject.basic.status} />
            </DetailField>
            <DetailField label='Fase' value={mappedProject.basic.phase}>
              <StatusTag value={mappedProject.basic.phase} />
            </DetailField>
            <DetailField label='Behandelende overheid' value={mappedProject.basic.behandelendeOverheid} />
            <DetailField label='Beroepsinstantie' value={mappedProject.basic.beroepsInstantie} />
            <DetailField label='Inzage gegevens type' value={mappedProject.basic.inzageGegevensType} />
            <DetailField label='Projectinhoud versie' value={mappedProject.basic.projectinhoudVersie} />
        </div>
      )}

      {activeTab === 'procedure' && (
        <div className='property-detail-page__phase-list'>
          {mappedProject.phases.map((phase) => {
            const hasDecision = phase.status === 'decision' || phase.fields.some(([label, value]) => label.toLowerCase().includes('decision date') && !isMissingValue(value))
            const isExpanded = expandedPhaseKey === phase.key

            return (
              <div className='property-detail-page__phase-line' key={phase.key}>
                <button
                  type='button'
                  className='property-detail-page__phase-line-header'
                  onClick={() => setExpandedPhaseKey(isExpanded ? null : phase.key)}
                >
                  <span>{phase.label}</span>
                  <span className={hasDecision ? 'decision-tag has-decision' : 'decision-tag'}>
                    {hasDecision ? 'decision' : 'no decision'}
                  </span>
                </button>
                {isExpanded && (
                  <div className='property-detail-page__phase-line-content'>
                    {phase.fields.map(([label, value, options]) => (
                      <DetailField key={`${phase.key}-${label}`} label={label} value={value}>
                        {options?.file !== undefined ? <DocumentFieldValue file={options.file} /> : undefined}
                      </DetailField>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {activeTab === 'request' && (
        <div className={`property-detail-page__request-layout ${selectedPart ? 'part-open' : ''}`}>
          <div className='property-detail-page__request-list'>
            {mappedProject.requestParts.length > 0 ? (
              mappedProject.requestParts.map((part, partIndex) => (
                <button
                  type='button'
                  className={`property-detail-page__request-card ${selectedPartKey === part.key ? 'selected' : ''}`}
                  key={part.key}
                  onClick={() => setSelectedPartKey(selectedPartKey === part.key ? null : part.key)}
                >
                  <div className='property-detail-page__request-card-title'>{part.title || `Request part ${partIndex + 1}`}</div>
                  <div className='property-detail-page__request-card-description'>
                    <FieldValue value={part.description} />
                  </div>
                </button>
              ))
            ) : (
              <ContentMissingTag />
            )}
          </div>

          <div className={`property-detail-page__part-section ${selectedPart ? 'expanded' : 'collapsed'}`}>
            {selectedPart ? (
              <div className='property-detail-page__part-shell'>
                <div className='property-detail-page__part-header'>
                  <h4>{selectedPart.title || 'Request part'}</h4>
                </div>

                <div className='property-detail-page__line-list'>
                  <DetailField label='Type' value={selectedPart.type} />
                  <DetailField label='Title' value={selectedPart.title} />
                  <DetailField label='Description' value={selectedPart.description} />
                  <DetailField label='Kadastrale percelen' value={selectedPart.capakeys}>
                    {selectedPart.capakeys.length > 0 ? selectedPart.capakeys.join(', ') : null}
                  </DetailField>
                </div>

                <div className='property-detail-page__block'>
                  <button
                    type='button'
                    className='property-detail-page__file-section-title'
                    onClick={() => toggleRequestSection('subparts')}
                  >
                    <span className='property-detail-page__section-title-text'>Subparts</span>
                    <span>{selectedPart.subparts.length}</span>
                  </button>
                  {expandedRequestSections.subparts && (
                    selectedPart.subparts.length > 0 ? (
                      <div className='property-detail-page__subpart-list'>
                        {selectedPart.subparts.map((subpart) => (
                          <div className='property-detail-page__subpart-card' key={subpart.key || subpart.title}>
                            <DetailField label='Planaanduiding' value={subpart.planAanduiding} />
                            <DetailField label='Type' value={subpart.type} />
                            <DetailField label='Handeling' value={subpart.handeling} />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <ContentMissingTag />
                    )
                  )}
                </div>

                <div className='property-detail-page__file-section'>
                  <button
                    type='button'
                    className='property-detail-page__file-section-title'
                    onClick={() => toggleRequestSection('plans')}
                  >
                    <span className='property-detail-page__section-title-text'>Plans and photos</span>
                    <span>{selectedPart.plansAndPhotos.length}</span>
                  </button>
                  {expandedRequestSections.plans && (
                    selectedPart.plansAndPhotos.length > 0 ? (
                      <div className='property-detail-page__file-list'>
                        {selectedPart.plansAndPhotos.map((file) => (
                          <FileRow file={file} key={file.uuid || file.object_key || file.bestandsnaam} />
                        ))}
                      </div>
                    ) : (
                      <ContentMissingTag />
                    )
                  )}
                </div>

                <div className='property-detail-page__file-section'>
                  <button
                    type='button'
                    className='property-detail-page__file-section-title'
                    onClick={() => toggleRequestSection('dossierstukken')}
                  >
                    <span className='property-detail-page__section-title-text'>Dossierstukken</span>
                    <span>{selectedPart.dossierstukken.length}</span>
                  </button>
                  {expandedRequestSections.dossierstukken && (
                    selectedPart.dossierstukken.length > 0 ? (
                      <div className='property-detail-page__dossier-list'>
                        {selectedPart.dossierstukken.map((stuk) => {
                          const dossierKey = `dossier-${stuk.uuid}`
                          return (
                            <div className='property-detail-page__dossier-item' key={stuk.uuid}>
                              <button
                                type='button'
                                className='property-detail-page__dossier-title'
                                onClick={() => toggleRequestSection(dossierKey)}
                              >
                                <span>{DOSSIERSTUK_TYPE_LABELS[stuk.dossierstuk_type_code] || stuk.dossierstuk_type_code || 'Dossierstuk'}</span>
                                <span>{stuk.files.length} files</span>
                              </button>
                              {expandedRequestSections[dossierKey] && (
                                stuk.files.length > 0 ? (
                                  <div className='property-detail-page__file-list'>
                                    {stuk.files.map((file) => (
                                      <FileRow file={file} key={`${stuk.uuid}-${file.uuid || file.object_key || file.bestandsnaam}`} />
                                    ))}
                                  </div>
                                ) : (
                                  <ContentMissingTag />
                                )
                              )}
                            </div>
                          )
                        })}
                      </div>
                    ) : (
                      <ContentMissingTag />
                    )
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  )
}

function PropertyDetailPage() {
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(false)
  const [loadingExport, setLoadingExport] = useState(false)
  const [propertyRecord, setPropertyRecord] = useState(location.state?.propertyRecord || null)
  const [historicExport, setHistoricExport] = useState(null)
  const [selectedProjectKey, setSelectedProjectKey] = useState(null)
  const [projectTab, setProjectTab] = useState('basic')
  const [expandedPhaseKey, setExpandedPhaseKey] = useState(null)
  const [selectedPartKey, setSelectedPartKey] = useState(null)
  const propertyId = searchParams.get('propertyId')
  const capakey = searchParams.get('capakey')

  useEffect(() => {
    const fetchProperty = async () => {
      if (propertyRecord || (!propertyId && !capakey)) return

      setLoading(true)
      try {
        const response = await httpClient.get(import.meta.env.VITE_API_URL + '/api/properties/get')
        if (response.status === 200) {
          const responseProperties = response.data || []
          const matchedProperty = propertyId
            ? responseProperties.find((property) => String(property.id) === String(propertyId))
            : (() => {
                const capakeyMatches = responseProperties.filter(
                  (property) => capakey && normalizeCapakey(property.cadastral_nr) === normalizeCapakey(capakey)
                )
                return capakeyMatches.length === 1 ? capakeyMatches[0] : null
              })()

          setPropertyRecord(matchedProperty || null)
        }
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to load property detail')
      } finally {
        setLoading(false)
      }
    }

    fetchProperty()
  }, [capakey, propertyId, propertyRecord])

  const hasData = useMemo(() => {
    if (typeof location.state?.hasData === 'boolean') {
      return location.state.hasData
    }

    return resolvePropertyDataStatus(propertyRecord)
  }, [location.state?.hasData, propertyRecord])

  useEffect(() => {
    const fetchHistoricExport = async () => {
      const targetCapakey = propertyRecord?.cadastral_nr || capakey
      if (!targetCapakey) return

      setLoadingExport(true)
      try {
        const response = await httpClient.get(
          import.meta.env.VITE_API_URL + `/api/properties/historic-data-export?capakey=${encodeURIComponent(targetCapakey)}`
        )

        setHistoricExport(response.data || null)
        console.log('Historic data export for capakey:', targetCapakey, response.data)
      } catch (error) {
        setHistoricExport(null)
        if (hasData || error.response?.status !== 404) {
          toast.error(error.response?.data?.message || 'Failed to load historic data export')
        }
      } finally {
        setLoadingExport(false)
      }
    }

    fetchHistoricExport()
  }, [capakey, hasData, propertyRecord?.cadastral_nr])

  const displayCapakey = propertyRecord
    ? formatPropertyIdentifier(propertyRecord.cadastral_nr, propertyRecord.partition_nr)
    : capakey || 'Unknown property'
  const validationStatus = propertyRecord?.cadastral_validation_status || location.state?.validation?.status || 'unvalidated'
  const displayLocation = propertyRecord?.location || location.state?.validation?.suggested_property_updates?.location || 'No location available yet'
  const mappedProjects = useMemo(() => mapHistoricProjects(historicExport), [historicExport])
  const selectedProject = mappedProjects.find((project) => project.key === selectedProjectKey) || null

  const handleProjectClick = (projectKey) => {
    setSelectedProjectKey((currentKey) => {
      const nextKey = currentKey === projectKey ? null : projectKey
      setProjectTab('basic')
      setExpandedPhaseKey(null)
      setSelectedPartKey(null)
      return nextKey
    })
  }

  return (
    <div className='template'>
      <Header />
      <MenuBar />

      <div className={`template__body ${selectedProject ? 'project-open' : ''}`} id='body-property-detail'>
        <div className='body__section big property-detail-page__overview-section'>
          {loading ? (
            <div className='property-detail-page__loading'>
              <CircularProgress color='inherit' />
            </div>
          ) : (
            <>
              <div className='property-detail-page__hero'>
                <div>
                  <h3>{displayCapakey}</h3>
                  <p>{displayLocation}</p>
                </div>
              </div>

              <div className='property-detail-page__overview-panel'>
                <h4>Projects</h4>
                {loadingExport ? (
                  <div className='property-detail-page__loading property-detail-page__loading--embedded'>
                    <CircularProgress color='inherit' />
                  </div>
                ) : mappedProjects.length > 0 ? (
                  <div className='property-detail-page__project-list'>
                    {mappedProjects.map((project) => (
                      <button
                        type='button'
                        className={`property-detail-page__project-card ${selectedProjectKey === project.key ? 'selected' : ''}`}
                        key={project.key}
                        onClick={() => handleProjectClick(project.key)}
                      >
                        <div className='property-detail-page__project-card-main'>
                          <div className='property-detail-page__project-card-title'>{project.basic.projectNumber || 'Unknown project'}</div>
                          <div className='property-detail-page__project-card-description'>
                            {project.basic.description || ''}
                          </div>
                        </div>
                        <div className='property-detail-page__project-card-meta'>
                          <span>{project.basic.projectTypeLabel || project.basic.projectTypeCode || 'Unknown type'}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className='property-detail-page__empty'>No projects found for this property.</div>
                )}
              </div>
            </>
          )}
        </div>

        <div className={`body__section big property-detail-page__project-section ${selectedProject ? 'expanded' : 'collapsed'}`}>
          <div className='property-detail-page__project-shell'>
            <div className='property-detail-page__project-section-header'>
              <h4>Project data</h4>
            </div>
            {selectedProject ? (
              <ProjectDetailPanel
                mappedProject={selectedProject}
                activeTab={projectTab}
                setActiveTab={setProjectTab}
                expandedPhaseKey={expandedPhaseKey}
                setExpandedPhaseKey={setExpandedPhaseKey}
                selectedPartKey={selectedPartKey}
                setSelectedPartKey={setSelectedPartKey}
              />
            ) : (
              <div className='property-detail-page__empty'>Select a project to view the project data.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default PropertyDetailPage
